import { Fragment, useState, useCallback, useMemo, useEffect } from "react";
import Modal from "../elements/Modal";
import { lngDetect } from "../../helpers/lngDetect";
import Button from "../form/Button";
import Textarea from "../form/Textarea";
import { useSelector } from "react-redux";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import useForm from "../../hooks/useForm";

// Constants
const RATING_LABELS = {
  1: "Poor",
  2: "Fair",
  3: "Good",
  4: "Very Good",
  5: "Excellent",
};

// Validation rules
const validation = {
  rating: [
    {
      required: true,
      message: "Please select a rating",
      errorType: "required",
    },
  ],
  review: [
    {
      required: true,
      message: "Please write a review",
      errorType: "required",
    },
    {
      type: "range",
      min: 10,
      max: 1000,
      message: "Review must be between 10 and 1000 characters",
      errorType: "validation",
    },
  ],
};

const ReviewRatingModal = ({ isOpen, onClose = () => {}, event }) => {
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);

  const { data: usrData } = useSelector((state) => state?.profile);

  // Initialize useForm - following FormComponent pattern
  const {
    values,
    handleChange,
    handleSubmit,
    errors,
    setFieldsValue,
    formValidate,
  } = useForm({
    validationRules: validation,
    initialState: {
      rating: 0,
      review: "",
    },
  });

  // Reset form when modal opens/closes - following FormComponent pattern
  const handleClose = useCallback(() => {
    setRating(0);
    setHoverRating(0);
    setFieldsValue({
      rating: 0,
      review: "",
    });
    onClose();
  }, [onClose, setFieldsValue]);

  // Handle star rating click
  const handleStarClick = useCallback(
    (starRating) => {
      setRating(starRating);
      handleChange({
        target: { name: "rating", value: starRating },
      });
    },
    [handleChange]
  );

  // Handle star hover
  const handleStarHover = useCallback((starRating) => {
    setHoverRating(starRating);
  }, []);

  // Handle star mouse leave
  const handleStarLeave = useCallback(() => {
    setHoverRating(0);
  }, []);

  // Sync rating state with form values
  useEffect(() => {
    if (values.rating !== rating) {
      setRating(values.rating || 0);
    }
  }, [values.rating, rating]);

  // Get display rating
  const displayRating = hoverRating || rating || values.rating || 0;

  // Review submission mutation - following FormComponent pattern
  const { mutate, isPending: isLoading } = useMutation({
    mutationFn: async (val) => {
      if (!usrData) {
        throw new Error("Please login to submit a review");
      }

      // Prepare review data - following FormComponent payload pattern
      const payload = {
        ...val,
        eventId: event?._id,
        userId: usrData?._id,
        timestamp: new Date().toISOString(),
      };

      // Simulate API call - replace with actual review service
      await new Promise((resolve) => setTimeout(resolve, 1500));

      return {
        status: 200,
        message: "Review submitted successfully!",
        data: payload,
      };
    },
    onSuccess: (res) => {
      if (res?.status === 200 || res?.status === 201) {
        toast.success(res?.message);
        handleClose();
        return res;
      } else {
        toast.warning(res?.message || res?.error);
      }
      return res;
    },
    onError: (error) => {
      console.log(error);
      toast.error(error?.message || "Something went wrong. Please try again.");
    },
  });

  // Get user display info
  const userDisplayInfo = useMemo(() => {
    if (!usrData) return null;

    const name =
      usrData.name ||
      `${usrData.firstName || ""} ${usrData.lastName || ""}`.trim() ||
      "User";
    const initial = name.charAt(0).toUpperCase();

    return { name, initial };
  }, [usrData]);

  return (
    <Fragment>
      <Modal
        isOpen={isOpen}
        setIsOpen={handleClose}
        title={`Review ${lngDetect(event?.eventTitle) || "Event"}`}
        size="xl3"
        titleIcon="fa-regular fa-star"
        titleClass="!font-semibold !text-2xl !capitalize truncate max-w-[700px] w-full tracking-tight !text-deep-purple"
        headerClass="!text-black !rounded-t-2xl border-b border-surface-03 bg-primary/10 py-4 px-10"
        childrenClasses="!bg-white"
        bodyClasses={"!py-8 md:!px-10"}
      >
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <h3 className="text-xl font-semibold text-gray-800 mb-2">
              Share Your Experience
            </h3>
            <p className="text-gray-600">
              How was your experience with{" "}
              <span className="font-bold text-deep-purple">
                {lngDetect(event?.eventTitle)}
              </span>
              ?
            </p>
          </div>

          {/* Form following FormComponent pattern */}
          <form onSubmit={(event) => handleSubmit(event, mutate)}>
            <div className="grid grid-cols-12 gap-x-4 gap-y-4">
              {/* Star Rating Section */}
              <div className="col-span-12 text-center">
                <label className="block text-lg text-deep-purple capitalize font-semibold mb-4">
                  Rate this event
                  <span className="text-red-500 ml-1">*</span>
                </label>

                <div className="flex items-center justify-center gap-2 mb-4">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => handleStarClick(star)}
                      onMouseEnter={() => handleStarHover(star)}
                      onMouseLeave={handleStarLeave}
                      className="text-4xl transition-all duration-200 hover:scale-110 focus:outline-none"
                    >
                      <i
                        className={`fa-solid fa-star ${
                          star <= displayRating
                            ? "text-amber-400"
                            : "text-gray-300"
                        }`}
                      ></i>
                    </button>
                  ))}
                </div>

                {displayRating > 0 && (
                  <div className="text-lg font-medium text-amber-600 mb-2">
                    {RATING_LABELS[displayRating]}
                  </div>
                )}

                {rating > 0 && (
                  <div className="text-sm text-gray-500">
                    You rated this event {rating} out of 5 stars
                  </div>
                )}

                {/* Rating Error - following FormComponent pattern */}
                {formValidate(errors, "rating").errorText && (
                  <div className="text-xs text-red-500 mt-2 text-center">
                    <i className="fa-regular fa-fw fa-triangle-exclamation text-sm mr-1"></i>
                    {formValidate(errors, "rating").errorText}
                  </div>
                )}
              </div>

              {/* Review Text Area */}
              <div className="col-span-12">
                <Textarea
                  label="Write your review"
                  inputPlaceholder="Share your thoughts about this event. What did you like? What could be improved?"
                  inputName="review"
                  inputValue={values.review || ""}
                  onChange={handleChange}
                  rows={6}
                  maxLength={1000}
                  {...formValidate(errors, "review")}
                />
                <div className="text-right text-sm text-gray-500 mt-2">
                  {(values.review || "").length}/1000 characters
                </div>
              </div>

              {/* User Info Display */}
              {userDisplayInfo && (
                <div className="col-span-12">
                  <div className="p-4 bg-gray-50 rounded-xl">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-purple-500 rounded-full flex items-center justify-center">
                        <span className="text-white text-sm font-semibold">
                          {userDisplayInfo.initial}
                        </span>
                      </div>
                      <div>
                        <div className="font-medium text-gray-800">
                          {userDisplayInfo.name}
                        </div>
                        <div className="text-sm text-gray-600">
                          This review will be posted publicly
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Guidelines */}
              <div className="col-span-12">
                <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
                  <h4 className="font-semibold text-blue-800 mb-2">
                    <i className="fa-regular fa-lightbulb mr-2"></i>
                    Review Guidelines
                  </h4>
                  <ul className="text-sm text-blue-700 space-y-1">
                    <li>• Be honest and constructive in your feedback</li>
                    <li>• Focus on your personal experience</li>
                    <li>• Avoid offensive language or personal attacks</li>
                    <li>• Help others make informed decisions</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Action Buttons - following FormComponent pattern */}
            <div className="relative col-span-4 mt-5">
              <div className="flex items-center justify-center gap-4">
                <Button
                  buttonType="submit"
                  buttonLabel="Submit Review"
                  variant="secondary"
                  {...(isLoading && {
                    loading: true,
                    buttonIconPosition: "left",
                    isDisable: true,
                  })}
                />
                <Button
                  buttonLabel="Cancel"
                  variant="primary"
                  buttonFunction={handleClose}
                />
              </div>
            </div>
          </form>
        </div>
      </Modal>
    </Fragment>
  );
};

export default ReviewRatingModal;
